import React, { useState, useEffect, useRef } from 'react';
import Navbar from '../../components/Navbar/Navbar';
import Footer from '../../components/Footer/Footer';
import { Truck, RotateCcw, PackageCheck, ShieldCheck, HelpCircle, CheckCircle } from 'lucide-react';
import InternationalPhoneInput from '../../components/Common/InternationalPhoneInput/InternationalPhoneInput';
import './ShippingReturnsPage.css';

// Defined outside component — stable reference, no stale-closure risk.
const SHIPPING_SIDEBAR_ITEMS = [
  { label: 'Shipping & Delivery Charges', id: 'shipping-charges' },
  { label: 'How Shipping Works', id: 'how-shipping-works' },
  { label: 'Shipping Timeline', id: 'shipping-timeline' },
  { label: 'Return Policy', id: 'return-policy' },
  { label: 'Cancellation Policy', id: 'cancellation-policy' }
];

export default function ShippingReturnsPage() {
  const [activeSection, setActiveSection] = useState('shipping-charges');
  const [form, setForm] = useState({
    name: '',
    phone: '',
    countryCode: '+91',
    email: '',
    city: '',
    message: ''
  });

  const sectionsRef = useRef({});
  const sidebarItems = SHIPPING_SIDEBAR_ITEMS;

  const [sidebarState, setSidebarState] = useState('top'); // 'top' | 'fixed' | 'docked'
  const mainLayoutRef = useRef(null);
  const sidebarNavRef = useRef(null);

  // Functional Sidebar ScrollSpy using IntersectionObserver
  useEffect(() => {
    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    const observerOptions = {
      root: null,
      rootMargin: '-90px 0px -50% 0px',
      threshold: 0
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    SHIPPING_SIDEBAR_ITEMS.forEach((item) => {
      const el = document.getElementById(item.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  // Three-phase sticky: 'top' (in flow) -> 'fixed' (pinned below navbar)
  // -> 'docked' (absolutely positioned at bottom of the column so it
  // never overlaps the footer).
  // Functionality unchanged — only the CSS values that size/position
  // the sidebar have been updated to match the Growth page reference.
  useEffect(() => {
    const TOP_OFFSET = 118;
    let sidebarHeight = 0;

    const checkSidebarState = () => {
      const container = mainLayoutRef.current;
      if (!container) return;
      if (!sidebarHeight) {
        sidebarHeight = container.querySelector('.sidebar-inner')?.offsetHeight || 0;
      }
      const rect = container.getBoundingClientRect();

      const navEl = sidebarNavRef.current;
      if (navEl) {
        const navRect = navEl.getBoundingClientRect();
        container.style.setProperty('--sidebar-fixed-left', `${navRect.left}px`);
      }

      let nextState;
      if (rect.top > TOP_OFFSET) {
        nextState = 'top';
      } else if (rect.bottom < TOP_OFFSET + sidebarHeight) {
        nextState = 'docked';
      } else {
        nextState = 'fixed';
      }

      setSidebarState((prev) => (prev === nextState ? prev : nextState));
    };

    checkSidebarState();
    window.addEventListener('scroll', checkSidebarState, { passive: true });
    window.addEventListener('resize', checkSidebarState, { passive: true });
    return () => {
      window.removeEventListener('scroll', checkSidebarState);
      window.removeEventListener('resize', checkSidebarState);
    };
  }, []);

  const handleSidebarClick = (id) => {
    setActiveSection(id);
    const targetElement = document.getElementById(id);
    if (targetElement) {
      const offset = 90;
      const elementPosition = targetElement.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const handleInputChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    alert('Thank you for contacting Haatza! We will get in touch with you shortly.');
    setForm({ name: '', phone: '', countryCode: '+91', email: '', city: '', message: '' });
  };

  return (
    <div className="shipping-returns-wrapper">
      <Navbar />

      {/* SECTION 1: HERO */}
      <section className="shipping-hero-section">
        <div className="container shipping-hero-grid-2">
          <div className="hero-copy">
            <h1 className="hero-heading">
              <span className="highlight-blue">Haatza</span> Shipping, Delivery &amp;<br />Returns Policy
            </h1>
            <p className="hero-subtext">
              Get your products delivered to crores of customers with the <a href="#shipping-charges" className="highlight-blue">lowest shipping charges</a>.
            </p>
            <div className="hero-callout-simple sr-gst-unique-box">
              <span className="new-badge-pill">NEW</span>
              <div className="gst-callout-text sr-gst-unique-text-wrap">
                <div className="gst-callout-line sr-gst-unique-line1">Don't have a GSTIN or have a Composition GSTIN?</div>
                <div className="gst-callout-line sr-gst-unique-line2">You can still sell on Haatza. Click <a href="/donothavegst" className="highlight-blue underline font-semibold sr-gst-unique-link">here</a> to know more.</div>
              </div>
            </div>
            <a href="/signin" style={{ textDecoration: 'none' }}>
              <button className="hero-cta-btn">Start Selling</button>
            </a>
          </div>

          <div className="hero-visual-container">
            <img
              src="https://static.wixstatic.com/media/fe689e_ea1038508fb44ee2bbd04f6b9004ff00~mv2.jpg/v1/fill/w_870,h_534,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/9fc0470c25.jpg"
              alt="Haatza Shipping and Delivery Workspace"
              className="hero-boxes-img"
            />
          </div>
        </div>
      </section>

      {/* SECTION 2: DELIVER PRODUCTS ACROSS INDIA */}
      <section className="deliver-across-section">
        <div className="container deliver-flex-container">
          <div className="deliver-left-copy">
            <h2 className="deliver-heading sr-section-heading">Deliver products across India</h2>
            <p className="deliver-subtext sr-body-text">
              Haatza ensures quick and hassle-free delivery of all your products<br />across India
            </p>
          </div>

          <div className="stats-row-3">
            <div className="stat-pill-card sr-stat-pill-card">
              <div className="stat-icon bg-blue-100 text-blue-600 sr-stat-icon-wrapper">
                <img src="https://static.wixstatic.com/media/fe689e_29e581a64ccb4decaeb0415ae3d6c9f3~mv2.png/v1/fill/w_74,h_57,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/Screenshot%202025-01-08%20at%207_edited.png" alt="10 crore+ Haatza customers" className="stat-icon-img sr-stat-icon-img" />
              </div>
              <p className="stat-pill-text">10 crore+ Haatza customers</p>
            </div>
            <div className="stat-pill-card sr-stat-pill-card">
              <div className="stat-icon bg-green-100 text-green-600 sr-stat-icon-wrapper">
                <img src="https://static.wixstatic.com/media/fe689e_40e20dbf7f7f4bff8c992a94923d3fab~mv2.png/v1/fill/w_74,h_72,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/Screenshot%202025-01-08%20at%209_edited.png" alt="7 days secure & regular payments" className="stat-icon-img sr-stat-icon-img" />
              </div>
              <p className="stat-pill-text">7* days secure & regular payments</p>
            </div>
            <div className="stat-pill-card sr-stat-pill-card">
              <div className="stat-icon bg-yellow-100 text-yellow-600 sr-stat-icon-wrapper">
                <img src="https://static.wixstatic.com/media/fe689e_f88547cc5cf44cca946ebb954edd5091~mv2.png/v1/fill/w_68,h_74,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/Screenshot%202025-01-08%20at%209_edited.png" alt="Low cost of doing business" className="stat-icon-img sr-stat-icon-img" />
              </div>
              <p className="stat-pill-text">Low cost of doing business</p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3: SCROLL-SPY LAYOUT (Sidebar + Content) */}
      <div className="shipping-main-layout" ref={mainLayoutRef}>

        {/* Sticky Left Sidebar — visually matched to the Growth page reference */}
        <aside
          ref={sidebarNavRef}
          className={`shipping-sidebar-nav ${sidebarState === 'fixed' ? 'shipping-sidebar-nav--fixed' : ''} ${sidebarState === 'docked' ? 'shipping-sidebar-nav--docked' : ''}`}
        >
          <div className="sidebar-inner">
            <ul className="sidebar-links">
              {sidebarItems.map((item) => (
                <li key={item.id}>
                  <button
                    onClick={() => handleSidebarClick(item.id)}
                    className={`sidebar-link-btn ${activeSection === item.id ? 'active' : ''}`}
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        {/* Right Scrollable Content */}
        <main className="shipping-content-container">

          {/* SECTION 4: SHIPPING & DELIVERY CHARGES */}
          <section
            className="shipping-charges-card shadow-sm border section-anchor"
            id="shipping-charges"
            ref={(el) => (sectionsRef.current['shipping-charges'] = el)}
          >
            <div className="shipping-heading-box">
              <h3 className="sr-charges-heading sr-section-heading shipping-returns-heading-23">
                <span className="sr-heading-line"></span>
                <span>Haatza Shipping & Delivery Charges</span>
              </h3>
            </div>
            <p className="section-intro-text sr-body-text mb-3">
              Haatza offers the lowest shipping cost across all online platforms in India. We have tied up with multiple Logistics Partners who pick up the product from your location and deliver it straight to the customer.
            </p>
            <p className="section-intro-text sr-body-text">
              With such low delivery charges, you can receive orders from all over India and focus more on selling your products online than handling the stress of shipping and logistics all by yourself.
            </p>
          </section>

          {/* SECTION 5: HOW TO SHIP YOUR ORDERS IN 3 SIMPLE STEPS */}
          <section className="ship-steps-card shadow-sm border section-anchor">
            <div className="shipping-heading-box">
              <h3 className="sr-steps-heading sr-section-heading shipping-returns-heading-23">
                <span className="sr-heading-line"></span>
                <span>How to Ship Your Orders in 3 Simple Steps</span>
              </h3>
            </div>

            <div className="ship-steps-grid-3">
              <div className="ship-step-item">
                <h4 className="step-title">Manage and process your orders through Haatza Supplier Panel</h4>
                <p className="step-desc sr-body-text">
                  Log on to the Haatza Supplier Panel and accept your order. Once done, you will have to download and print the Label and Manifest.
                </p>
              </div>

              <div className="ship-step-item">
                <h4 className="step-title">Pack your product and keep it ready for pickup</h4>
                <p className="step-desc sr-body-text">
                  Pack the product properly in plain packaging material with no branding. Haatza does not provide any packaging material. Please ensure that the product is properly packed and paste the label on the packaging.
                </p>
              </div>

              <div className="ship-step-item">
                <h4 className="step-title">Hand over the product to Haatza Delivery Partner</h4>
                <p className="step-desc sr-body-text">
                  Our logistics partner will pick up the order from your pickup address and deliver it directly to the customer.
                </p>
              </div>
            </div>
          </section>

          {/* SECTION 6: HOW SHIPPING WORKS */}
          <section
            className="how-shipping-works-card shadow-sm border section-anchor"
            id="how-shipping-works"
            ref={(el) => (sectionsRef.current['how-shipping-works'] = el)}
          >

            <div className="sit-back-callout sr-body-text">
              Sit back and relax while Haatza delivers your products safely and quickly. Customers love it when their products are packed well and delivered on time, and a happy customer service means a higher number of returning customers and orders for you.
            </div>

            <div className="youtube-player-wrapper">
              <iframe
                src="https://www.youtube.com/embed/XZKYAJTId6U"
                title="How to List Products on Haatza | Haatza Seller"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="embed-iframe"
              ></iframe>
            </div>
          </section>

          {/* SECTION 7: SHIPPING TIMELINE */}
          <section
            className="shipping-timeline-card shadow-sm border section-anchor"
            id="shipping-timeline"
            ref={(el) => (sectionsRef.current['shipping-timeline'] = el)}
          >
            <div className="shipping-heading-box">
              <h3 className="sr-timeline-heading sr-section-heading shipping-returns-heading-23">
                <span className="sr-heading-line"></span>
                <span>Shipping Timeline</span>
              </h3>
            </div>
            <p className="section-intro-text sr-body-text">
              The orders must be shipped within 2-3 days from the date of receiving the order within the agreed time provided by you (also known as Service Level Agreement or SLA). You can check the status of your order and the days left for dispatch on the Haatza Seller Panel.
            </p>
          </section>

          {/* SECTION 8: RETURN POLICY */}
          <section
            className="return-policy-card shadow-sm border section-anchor"
            id="return-policy"
            ref={(el) => (sectionsRef.current['return-policy'] = el)}
          >
            <div className="shipping-heading-box">
              <h3 className="sr-return-heading sr-section-heading shipping-returns-heading-20">
                <span className="sr-heading-line"></span>
                <span>Haatza Return Policy</span>
              </h3>
            </div>
            <p className="section-intro-text sr-body-text mb-3">
              Customers can return the product within 7 days from the date of delivery and the shipments which weren't delivered to the customer get converted to an RTO (Return to Origin).
            </p>
            <p className="section-intro-text sr-body-text mb-6">
              Haatza gives sellers all the information they need to manage their customer returns or RTOs (Return to Origin) along with details on compensation and charges levied for each return/RTO. We have best-in-class tools to help our sellers track and manage their returns, get order or product level data as well as detailed shipping tracking, payment and order history.
            </p>

            <h4 className="sub-title sr-sub-heading shipping-returns-heading-21 mb-4">Return Shipping Fee</h4>
            <div className="return-fee-list">
              <div className="fee-stacked-card">
                <p className="fee-text sr-body-text">
                  <span className="fee-inline-num">1. </span>If a customer returns a product, then the seller is charged a return shipping fee based on the weight of the shipment.
                </p>
              </div>

              <div className="fee-stacked-card">
                <p className="fee-text sr-body-text">
                  <span className="fee-inline-num">2. </span>If an order is not delivered to the customer and is returned to origin (RTO), the seller shall be liable for any applicable additional shipping charges.
                </p>
              </div>

              <div className="fee-stacked-card">
                <p className="fee-text sr-body-text">
                  <span className="fee-inline-num">3. </span>All return & RTO related charges can be tracked & managed using different tools that are provided to Haatza sellers.
                </p>
              </div>

              <div className="fee-stacked-card">
                <p className="fee-text sr-body-text">
                  <span className="fee-inline-num">4. </span>Please note that for some exceptional cases, the return shipping fees can be waived off.
                </p>
              </div>
            </div>
          </section>

          {/* SECTION 9: CANCELLATION POLICY */}
          <section
            className="cancellation-policy-card shadow-sm border section-anchor"
            id="cancellation-policy"
            ref={(el) => (sectionsRef.current['cancellation-policy'] = el)}
          >
            <div className="shipping-heading-box">
              <h3 className="sr-cancellation-heading sr-section-heading shipping-returns-heading-20">
                <span className="sr-heading-line"></span>
                <span>Cancellation Policy on Haatza</span>
              </h3>
            </div>
            <h4 className="sub-title sr-sub-heading shipping-returns-heading-21 mt-2 mb-3">No Cancellation Penalty</h4>
            <p className="section-intro-text sr-body-text">
              In order to maintain a good customer experience for customers, it is important for you to ensure that the products you have listed on Haatza are in stock and available to ship. However, we understand that there could be delays that are out of your control leading to late dispatch or order cancellation. In that case, Haatza will not charge any penalty to you so you can continue to sell online without any tension.
            </p>
          </section>

        </main>
      </div>

      {/* SECTION 9b: CTA BANNER */}
      <section className="shipping-cta-banner-section">
        <div className="container">
          <div className="shipping-cta-banner-inner">
            <div className="shipping-cta-left">
              <h2 className="shipping-cta-heading">
                Sell Products Online at 0% Commission on <span className="highlight-blue">Haatza</span>
              </h2>
            </div>
            <div className="shipping-cta-right">
              <p className="shipping-cta-subtext sr-body-text">
                Join the fastest growing e-commerce platform in India and sell to crores of users and grow your business.
              </p>
              <a href="/signin" style={{ textDecoration: 'none' }}>
                <button className="lead-cta-btn mt-3">Start Selling</button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 10: SELL PRODUCTS / KEEP IN TOUCH FORM */}
      <section className="shipping-lead-form-section light-blue-bg border-t border-b">
        <div className="container">
          <div className="lead-grid-2">
            <div className="lead-copy">
              <h2 className="lead-title">
                Sell Products Online at 0% Commission on <span style={{ color: '#0f172a' }}>Haatza</span>
              </h2>
              <p className="lead-text sr-body-text">
                Join the fastest growing e-commerce platform in India and sell to crores of users and grow your business.
              </p>
              <a href="/signin" style={{ textDecoration: 'none' }}>
                <button className="lead-cta-btn">Start Selling</button>
              </a>
            </div>

            <div className="lead-form-wrapper">
              <h3 className="form-wrapper-title">Keep in touch</h3>
              <form onSubmit={handleFormSubmit} className="lead-form-element">
                <div className="form-field">
                  <label>Name*</label>
                  <input
                    type="text"
                    required
                    placeholder="Enter your first name"
                    value={form.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                  />
                </div>

                <div className="form-field">
                  <label>Phone*</label>
                  <InternationalPhoneInput
                    phone={form.phone}
                    onChangePhone={(val) => setForm(prev => ({ ...prev, phone: val }))}
                    countryCode={form.countryCode}
                    onChangeCountryCode={(code) => setForm(prev => ({ ...prev, countryCode: code }))}
                    required
                  />
                </div>

                <div className="form-field">
                  <label>Email</label>
                  <input
                    type="email"
                    placeholder="Enter email address"
                    value={form.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                  />
                </div>

                <div className="form-field">
                  <label>City*</label>
                  <input
                    type="text"
                    required
                    placeholder="Enter city"
                    value={form.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                  />
                </div>

                <div className="form-field">
                  <label>Message</label>
                  <textarea
                    rows="3"
                    placeholder="Write your query here..."
                    value={form.message}
                    onChange={(e) => handleInputChange('message', e.target.value)}
                  ></textarea>
                </div>

                <button type="submit" className="form-submit-btn">Submit</button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}